import { errorDefinition, problemBody } from '@twin/contracts/errors';

export class AppError extends Error {
  constructor(problem, options = {}) {
    super(problem.message || problem.error || 'The action failed', options);
    this.name = 'AppError';
    Object.assign(this, problem);
  }
}

export function toAppError(value, fallbackCode = 'INTERNAL_ERROR') {
  if (value instanceof AppError) return value;
  if (value && typeof value === 'object' && value.code && (value.message || value.error)) {
    return new AppError(problemBody(value.code, value));
  }
  const definition = errorDefinition(fallbackCode);
  return new AppError(problemBody(fallbackCode, {
    technicalDetail: value?.stack || String(value || ''),
    ...definition,
  }), value instanceof Error ? { cause: value } : undefined);
}

export async function appErrorFromResponse(response, fallbackCode = 'INTERNAL_ERROR') {
  let payload = null;
  try {
    const type = response.headers?.get?.('content-type') || '';
    payload = (type.includes('json') || typeof response.text !== 'function')
      ? await response.json()
      : { technicalDetail: await response.text() };
  } catch { /* unreadable/non-JSON response */ }
  const code = payload?.code || (
    response.status === 401 ? 'AUTH_REQUIRED'
      : response.status === 403 ? 'FORBIDDEN'
        : response.status === 404 ? 'NOT_FOUND'
          : response.status === 413 ? 'UPLOAD_TOTAL_TOO_LARGE'
            : response.status >= 500 ? 'INTERNAL_ERROR'
              : fallbackCode
  );
  return new AppError(problemBody(code, {
    ...payload,
    message: payload?.message || payload?.error,
    requestId: payload?.requestId || response.headers?.get?.('x-request-id') || undefined,
  }));
}

export function appErrorFromXhr(xhr, fallbackCode = 'INTERNAL_ERROR') {
  let payload = null;
  try { payload = JSON.parse(xhr.responseText || 'null'); } catch { /* non-JSON edge response */ }
  const code = payload?.code || (
    xhr.status === 401 ? 'AUTH_REQUIRED'
      : xhr.status === 403 ? 'FORBIDDEN'
        : xhr.status === 413 ? 'UPLOAD_TOTAL_TOO_LARGE'
          : xhr.status >= 500 ? 'INTERNAL_ERROR'
            : fallbackCode
  );
  return new AppError(problemBody(code, {
    ...payload,
    message: payload?.message || payload?.error,
    requestId: payload?.requestId || xhr.getResponseHeader('x-request-id') || undefined,
  }));
}

const recentlyReported = new Map();

export function reportClientError(value, context = {}) {
  const error = toAppError(value, context.code || 'INTERNAL_ERROR');
  const key = `${error.code}:${error.message}:${context.source || ''}`;
  const now = Date.now();
  if (now - (recentlyReported.get(key) || 0) < 30_000) return error;
  recentlyReported.set(key, now);

  // A structured API error was already persisted by the server that returned
  // it. Only report failures that originated in the browser or network.
  if (!error.requestId || context.force) {
    fetch('/api/client-errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code: error.code,
        title: error.title,
        message: error.message,
        action: error.action,
        requestId: error.requestId,
        technicalDetail: String(context.technicalDetail || error.cause?.stack || value?.stack || '').slice(0, 2000),
      }),
    }).catch(() => {});
  }
  return error;
}
